// Context
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import apiAdmin from "../../services/apiAdmin";
import api from "../../services/api";
import Form from "../common/Form";
import { useChats } from "../../hooks/useChats";
import { useNoteType } from "../../hooks/useNoteType";
import { useFeatures } from "../../hooks/useFeatures";
import { useTranslation } from "react-i18next";

function ModalTicketCreate({ close, initialChatId }) {
  const { t } = useTranslation();
  const { chats } = useChats();
  const { noteTypesMap } = useNoteType();
  const { hasFeature } = useFeatures();
  const [ticket, setTicket] = useState(initialChatId ? { _chat: initialChatId } : {});
  const [selectedCategory, setSelectedCategory] = useState(null);

  const isEncountersUser = hasFeature("encounters");
  const isOrdersUser = hasFeature("orders") || hasFeature("ordersManagement");

  const hasBoth = isEncountersUser && isOrdersUser;

  const encountersList = isEncountersUser
    ? chats.map((chat) => ({
        key: chat.id,
        label: `${hasBoth ? "ENC " : ""}${chat.id} (${noteTypesMap[chat.contextVariables["local:encounterType"]]?.label}) - ${chat.user?.lastName} ${chat.user?.firstName}`,
      }))
    : [];

  const [ordersList, setOrdersList] = useState([]);

  useEffect(() => {
    if (!isOrdersUser) return;
    const fetchOrders = async () => {
      try {
        const apiCall = hasFeature("ordersManagement") ? api.getOrdersManufacturer() : api.getOrders();
        const data = await apiCall;
        if (!data.data.output.error && data.data.output.orders) {
          setOrdersList(
            data.data.output.orders.map((order) => ({
              key: order.id,
              label: `${hasBoth ? "ORD " : ""}${order.id} - ${order.patientLastName} ${order.patientFirstName}`,
            }))
          );
        }
      } catch (e) {
        console.error("Failed to fetch orders for ticket", e);
      }
    };
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listAutocomplete = [...encountersList, ...ordersList];

  const initialEncounterLabel = initialChatId ? listAutocomplete.find((item) => item.key === initialChatId)?.label : undefined;

  const autocompleteLabel =
    isEncountersUser && isOrdersUser
      ? t("tickets.create.encounterOrOrder")
      : isOrdersUser
        ? t("tickets.create.order")
        : t("tickets.create.encounter");

  const setTicketValue = (key, value) => {
    ticket[key] = value;
    setTicket({ ...ticket });
  };

  const selectCategory = (category) => {
    setSelectedCategory(category);
    setTicketValue("description", category);
  };

  const create = async () => {
    let req;
    try {
      req = await apiAdmin.createTicket(ticket);
    } catch (e) {
      // The admin interceptor throws on every non-2xx, so the else branch below
      // never sees a failure: without this catch the rejection was unhandled and
      // the clinician got neither the ticket nor a word about it. Message only --
      // never the raw axios error, whose config.data is the ticket description.
      console.error("Ticket create failed -", e?.message || e);
      toast.error(t("tickets.create.errorCreating"));
      return;
    }
    if (req.status === 200) {
      close();
    } else {
      toast.error(t("tickets.create.errorCreating"));
    }
  };

  const categories = t("tickets.create.categories", { returnObjects: true });

  return (
    <>
      <div className="ticket-create">
        <div className="ticket-categories">
          <label>{t("tickets.create.issueType")}</label>
          <div className="ticket-category-chips">
            {categories.map((category) => (
              <button
                key={category}
                className={"badge clickable" + (selectedCategory === category ? " blue" : "")}
                onClick={() => selectCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <Form
          fields={[
            {
              label: t("tickets.create.additionalDetails"),
              type: "textarea",
              value: ticket.description,
              onChange: (e) => {
                setSelectedCategory(null);
                setTicketValue("description", e.target.value);
              },
            },
            {
              label: autocompleteLabel,
              type: "autocomplete",
              data: listAutocomplete,
              value: ticket._chat,
              initValue: initialEncounterLabel,
              onSelect: (e) => setTicketValue("_chat", e?.key),
            },
            {
              label: t("tickets.create.requestPhoneCall"),
              type: "switch",
              value: ticket.requestPhoneCall || false,
              onChange: (checked) => setTicketValue("requestPhoneCall", checked),
            },
          ]}
        />
      </div>

      <div className="modal-actions">
        <button className="btn btn-primary" onClick={create} disabled={!ticket.description}>
          {t("tickets.create.create")}
        </button>
      </div>
    </>
  );
}

export default ModalTicketCreate;
